// The FRAGO (fragmentary order) card's deterministic fallback parser: spec §6 "fallbacks ... free-text
// FRAGO parsing has none [no sensible non-AI fallback], so its fallback is to reject and point the
// player at the deck" describes the general rule, but plan Task 18 asks for a small pattern parser too,
// "so the demo never dead-ends" when the model is slow or unreachable in the room. It only recognizes one
// literal shape - "<callsign> <move|attack|defend|screen|withdraw> <PL|OBJ|NAI name>" - and returns
// whatever it cannot map in `unparsed`, exactly like the LLM path (`buildFragoPrompt` in
// `src/ai/feedPrompts.ts`), so the confirmation UI in `src/ui/deck/FragoCard.tsx` treats both the same way.

import type { z } from 'zod';
import type { Graphic, Unit } from '../sim/types';
import type { Grid } from '../sim/grid';
import type { FragoSchema } from './schemas';
import { graphicPoint, nearestPassableCell } from './templates';

export interface FragoParseContext {
  units: Unit[];
  graphics: Graphic[];
  grid: Grid | null;
}

type FragoOrder = z.infer<typeof FragoSchema>['orders'][number];

const VERB_CARD: Record<string, FragoOrder['card']> = {
  move: 'move',
  attack: 'attack',
  defend: 'defend',
  screen: 'screen',
  withdraw: 'withdraw',
};

const LINE_RE = /^(\S+)\s+(move|attack|defend|screen|withdraw)\s+(.+)$/i;

/** Finds the named control graphic in `graphics` - an exact (case-insensitive) name match first, then
 * the graphic whose name is contained in the given text (tolerates trailing words the strict pattern
 * still matched into the target group, e.g. a trailing "now" or punctuation). */
function findGraphic(graphics: Graphic[], raw: string): Graphic | undefined {
  const norm = raw.trim().replace(/[.,;:!?]+$/, '').toUpperCase();
  if (norm === '') return undefined;
  const exact = graphics.find((g) => g.name.toUpperCase() === norm);
  if (exact) return exact;
  return graphics.find((g) => norm.includes(g.name.toUpperCase()));
}

function findUnit(units: Unit[], callsign: string): Unit | undefined {
  const norm = callsign.trim().toUpperCase();
  return units.find((u) => u.callsign.toUpperCase() === norm);
}

/**
 * Parses one order per line (lines split on newlines or semicolons) against the literal pattern
 * "<callsign> <move|attack|defend|screen|withdraw> <PL|OBJ|NAI name>". `screen` targets the graphic
 * directly (its card's target kind); the maneuver point cards aim at the nearest passable cell to the
 * named graphic, the same way `templates.ts`'s advisor fallbacks pick a legal maneuver point. Anything
 * that does not match - an unknown callsign, an unknown graphic, or a line that isn't in this shape at
 * all - is kept verbatim in `unparsed`, joined with "; ", never silently dropped.
 */
export function parseFragoDeterministic(text: string, ctx: FragoParseContext): z.infer<typeof FragoSchema> {
  const orders: FragoOrder[] = [];
  const unparsed: string[] = [];

  const lines = text
    .split(/[\n;]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const source = lines.length > 0 ? lines : [text.trim()].filter((s) => s.length > 0);

  for (const line of source) {
    const m = line.match(LINE_RE);
    if (!m) { unparsed.push(line); continue; }
    const [, csRaw, verbRaw, targetRaw] = m;

    const unit = findUnit(ctx.units, csRaw);
    if (!unit) { unparsed.push(line); continue; }

    const card = VERB_CARD[verbRaw.toLowerCase()];
    const graphic = findGraphic(ctx.graphics, targetRaw);
    if (!graphic) { unparsed.push(line); continue; }

    if (card === 'screen') {
      orders.push({ unitId: unit.id, card, target: { kind: 'graphic', graphicId: graphic.id } });
      continue;
    }

    if (!ctx.grid) { unparsed.push(line); continue; }
    const pt = graphicPoint(graphic, { x: unit.x, y: unit.y });
    const cell = nearestPassableCell(ctx.grid, pt);
    orders.push({ unitId: unit.id, card, target: { kind: 'point', cell } });
  }

  return { orders, unparsed: unparsed.length > 0 ? unparsed.join('; ') : undefined };
}
